/**
 * The comment-limit what-if (frame 132:555, `owner-decisions-align-132-555-03`):
 * a slider from "as capped today" (0) to "as if the per-post comment limit
 * were removed" (100). Local only — nothing is written, no campaign field,
 * no write route.
 *
 * This component prices nothing: it reports a percentage, and `CostEstimate`
 * derives the recomputed figures from `estimate.tail`'s own
 * `capped_credits_per_item`/`uncapped_credits_per_item` — numbers the server
 * already priced (§6.6 statement 1), never a value re-derived from the
 * arrival curve here. Same constraint `../../control/panel/Tail.tsx`
 * already carries (platform design §17.3).
 */

import type { ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { Slider } from '../../../ui/slider';
import { et } from './messages';

const MIN = 0;
const MAX = 100;

function clamp(value: number): number {
  return Math.min(MAX, Math.max(MIN, value));
}

export function CommentLimit(props: {
  value: number;
  onChange: (value: number) => void;
}): ReactNode {
  const locale = getLocale();
  // Radix reads `value` straight into `aria-valuenow` with no clamp of its
  // own, so the geometry (`Slider`'s `value`) and the announced text
  // (`getAriaValueText`) are each clamped from their own read of the prop —
  // one out-of-range value must not leave one path clamped and the other not.
  const geometryValue = clamp(props.value);
  const announcedValue = clamp(props.value);

  return (
    <div className="flex flex-col gap-2">
      <Slider
        min={MIN}
        max={MAX}
        value={[geometryValue]}
        onValueChange={(next) => props.onChange(next[0] ?? geometryValue)}
        aria-label={et('commentLimit.label')}
        getAriaValueText={() =>
          et('commentLimit.value', { percent: integer(announcedValue, locale) })
        }
      />
      <div className="flex justify-between text-caption text-muted-foreground">
        <span>{et('commentLimit.current')}</span>
        <span>{et('commentLimit.uncapped')}</span>
      </div>
    </div>
  );
}
